// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import { Session } from "@insite/client-framework/Services/SessionService";
import { BillToModel, CartModel } from "@insite/client-framework/Types/ApiModels";
import { formatCategories } from "@pacific/XDL/Utilities/helpers";

export const getDataLayer = () => {
    if (!(window as any).dataLayer) {
        (window as any).dataLayer = [];
    }

    return (window as any).dataLayer;
};

export const _GaSelectItem = ({
    productClickEvent,
    isAuthenticated,
    session,
    currentBillTo,
    productContext,
}: {
    productClickEvent?: boolean;
    isAuthenticated: boolean;
    session: any;
    currentBillTo: any;
    productContext: any;
}) => {
    if (productClickEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: "select_item",
            item_list_id: "ProductListPage",
            item_list_name: "Product List Page",
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            branch_number: (session as any)?.defaultWarehouse?.name || "",
            branch_name: (session as any)?.defaultWarehouse?.description || "",
            sales_rep: session.salesperson?.name || "",
            affiliation: currentBillTo?.companyName || "",
            items: [
                {
                    item_id: productContext.product.productNumber,
                    item_name: productContext.product.productTitle,
                    coupon: "",
                    discount: productContext.productInfo.pricing
                        ? productContext.productInfo.pricing.unitNetPrice <
                          productContext.productInfo.pricing.unitListPrice
                            ? productContext.productInfo.pricing.unitListPrice -
                              productContext.productInfo.pricing.unitNetPrice
                            : 0
                        : 0,
                    index: 0,
                    affiliation: currentBillTo?.companyName || "",
                    item_brand: productContext.product.brand?.name || "",
                    ...formatCategories(productContext.product.properties?.category),
                    item_list_id: "ProductListPage",
                    item_list_name: "Product List Page",
                    item_variant: "",
                    location_id: session?.pickUpWarehouse?.id || "",
                    price: productContext.productInfo.pricing?.unitNetPrice || 0,
                    quantity: 1,
                },
            ],
        };
        dataLayer.push(googleAnalyticsData);
    }
};

export const CHECKOUT_STEPS = {
    CART: 0,
    ADDRESS: 1,
    SHIPPING: 2,
    PAYMENT: 3,
    REVIEW: 4,
};

/* eslint-disable */
(function (window) {
    (window as any)._GaCheckoutFunnel = (
        step: number,
        cart: CartModel,
        session: Session,
        isAuthenticated: boolean,
        currentBillTo: BillToModel,
        discount?: number,
    ) => {
        const dataLayer = getDataLayer();

        if (!dataLayer || !cart.orderSubTotal) {
            return;
        }

        const googleAnalyticsData = {
            ...(step === CHECKOUT_STEPS.ADDRESS && {
                event: "begin_checkout",
            }),
            ...(step === CHECKOUT_STEPS.SHIPPING && {
                event: "add_shipping_info",
                shipping_tier: cart.carrier?.description || "",
            }),
            ...(step === CHECKOUT_STEPS.PAYMENT && {
                event: "add_payment_info",
                payment_type: cart.paymentMethod?.name || "",
            }),
            items: cart?.cartLines?.map((cartLine, index) => ({
                affiliation: currentBillTo?.companyName || "",
                item_id: cartLine.erpNumber,
                item_name: cartLine.shortDescription,
                price: cartLine.pricing?.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                item_brand: cartLine.brand?.name || "",
                ...formatCategories(cartLine.properties?.category),
                item_list_name: "",
                item_list_id: "",
                coupon: "",
                discount: cartLine.pricing
                    ? cartLine.pricing.unitNetPrice < cartLine.pricing.unitListPrice
                        ? cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice
                        : 0
                    : 0,
                index,
                item_variant: "",
                location_id: (session as any)?.pickUpWarehouse?.id || "",
            })),
            discount: discount || 0,
            value: cart.orderSubTotal,
            currency: session.currency?.currencyCode,
            coupon: cart.promotionCode || "",
            affiliation: currentBillTo?.companyName || "",
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            branch_number: (session as any)?.defaultWarehouse?.name || "",
            branch_name: (session as any)?.defaultWarehouse?.description || "",
            sales_rep: (session as any).salesperson?.name || "",
        };

        if (googleAnalyticsData.event !== "add_payment_info" || shouldPushToDataLayer(googleAnalyticsData)) {
            dataLayer.push(googleAnalyticsData);
        }
    };
})(typeof window != "undefined" ? window : {});
/* eslint-enable */

function shouldPushToDataLayer(googleAnalyticsData: any) {
    const dataLayer = getDataLayer();
    const lastEvent = dataLayer[dataLayer.length - 1];
    return lastEvent?.event !== "add_payment_info" || lastEvent.payment_type !== googleAnalyticsData.payment_type;
}
